import { useEffect, useMemo, useState } from 'react'
import {
  BasicInformatioTableDef,
  SearchOnEntitySearchCell,
} from '../../../../../containers/SingleSheetV2/table/model'
import {
  useCode,
  useDisplayName,
  useStatus,
  useSubStatus,
  useTags,
  useTeam,
  useAccountable,
  useResponsible,
  useAssignee,
  useWatchers,
  useDifficulty,
  usePriority,
  useEstimatedHour,
  useSprint,
  useScheduledDate,
  useActualDate,
  useCreatedBy,
  useCreatedAt,
  useUpdatedBy,
  useUpdatedAt,
  useRevision,
  useEstimatedStoryPoint,
  useType,
} from './cell-def/wbsItem'
import {
  useAssignment,
  useBasicInformation,
  usePlanning,
  useUpdatedInfo,
} from './table-header-def/wbsItem'
import { useWbsItemTagSearch } from '../../../hooks/wbsItemTagSearch'
import { WbsItemPropertySearch } from '../../../hooks/wbsItemPropertySearch'
import { WbsItemSelectOptions } from '../../../hooks/wbsItemSelectOptions'
import {
  useParentWbsItem,
  useTicketList,
  useTicketType,
} from './cell-def/ticket'
import { useBasicInformationTableComposer } from './table-composer'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import { FunctionPropertyConfigurationState } from '../../../hooks/functionPropertyConfiguration'

export const useTicketBasicInformationTableDef = (
  projectUuid: string,
  { subStatuses, difficulties, priorities }: WbsItemSelectOptions,
  { searchTeam, searchMember, searchSprint }: WbsItemPropertySearch,
  searchTicketList: SearchOnEntitySearchCell,
  wbsItemAdditionalProperties:
    | WbsItemAdditionalPropertyLayoutEntity
    | undefined,
  functionPropertyConfiguration: FunctionPropertyConfigurationState
): BasicInformatioTableDef => {
  // Table groups
  const basicInformationGroup = useBasicInformation()
  const assignmentGroup = useAssignment()
  const planningGroup = usePlanning()
  const updatedInfoGroup = useUpdatedInfo()
  // Hooks for table cells
  const searchTag = useWbsItemTagSearch(projectUuid)
  // Table cells
  const code = useCode()
  const ticketList = useTicketList(searchTicketList)
  const parentWbsItem = useParentWbsItem()
  const displayName = useDisplayName()
  const status = useStatus()
  const substatus = useSubStatus(subStatuses)
  const type = useType() // TODO Remove. please refer comments inside code.
  const ticketTypeCell = useTicketType() // TODO Remove. please refer comments inside code.
  const tags = useTags(searchTag)
  const team = useTeam(searchTeam)
  const accountable = useAccountable(searchMember)
  const responsible = useResponsible(searchMember)
  const assignee = useAssignee(searchMember)
  const watchers = useWatchers(searchMember)
  const difficulty = useDifficulty(difficulties)
  const priority = usePriority(priorities)
  const estimatedStoryPoint = useEstimatedStoryPoint()
  const estimatedHour = useEstimatedHour()
  const sprint = useSprint(searchSprint)
  const scheduledDate = useScheduledDate()
  const actualDate = useActualDate()
  const createdBy = useCreatedBy()
  const createdAt = useCreatedAt()
  const updatedBy = useUpdatedBy()
  const updatedAt = useUpdatedAt()
  const revision = useRevision()

  const defaultTableDef = useMemo<BasicInformatioTableDef>(() => {
    return {
      tables: [
        {
          header: basicInformationGroup,
          rows: [
            [ticketList, code],
            [parentWbsItem],
            [displayName],
            [status],
            [substatus],
            [type, ticketTypeCell],
            [tags],
          ],
        },
        {
          header: assignmentGroup,
          rows: [[team], [accountable], [responsible], [assignee], [watchers]],
        },
        {
          header: planningGroup,
          rows: [
            [difficulty, priority],
            [estimatedStoryPoint],
            [estimatedHour, sprint],
            [scheduledDate],
            [actualDate],
          ],
        },
        {
          header: updatedInfoGroup,
          rows: [[createdBy, createdAt], [updatedBy, updatedAt], [revision]],
        },
      ],
    }
  }, [
    basicInformationGroup,
    assignmentGroup,
    planningGroup,
    updatedInfoGroup,
    code,
    ticketList,
    parentWbsItem,
    displayName,
    status,
    substatus,
    type,
    ticketTypeCell,
    tags,
    team,
    accountable,
    responsible,
    assignee,
    watchers,
    difficulty,
    priority,
    estimatedStoryPoint,
    estimatedHour,
    sprint,
    scheduledDate,
    actualDate,
    createdBy,
    createdAt,
    updatedBy,
    updatedAt,
    revision,
  ])

  const basicInformationTableDef = useBasicInformationTableComposer(
    projectUuid,
    defaultTableDef,
    wbsItemAdditionalProperties,
    functionPropertyConfiguration
  )

  return basicInformationTableDef
}
